import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const API_URL = '';

// Testi UI per lingua
const uiStrings: Record<string, Record<string, string>> = {
  it: {
    title: 'Coach di Sicurezza O₃',
    placeholder: 'Scrivi un messaggio...',
    welcome: 'Ciao! Sono il tuo Coach di Sicurezza O₃. Chiedimi qualsiasi cosa sull\'ozono e il suo uso sicuro.',
    error: 'Mi dispiace, si è verificato un errore. Riprova.',
    poweredBy: 'Powered by ChatGPT',
    reset: 'Nuova conversazione',
  },
  en: {
    title: 'O₃ Safety Coach',
    placeholder: 'Write a message...',
    welcome: 'Hello! I am your O₃ Safety Coach. Ask me anything about ozone and its safe use.',
    error: 'Sorry, an error occurred. Please try again.',
    poweredBy: 'Powered by ChatGPT',
    reset: 'New conversation',
  },
};

export default function AIAssistant() {
  const { i18n } = useTranslation();
  const currentLang = (i18n.language === 'en' ? 'en' : 'it');
  const ui = uiStrings[currentLang];

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevLangRef = useRef(currentLang);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const startRecordingRef = useRef<() => void>(() => {});

  // Scroll automatico in basso
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus sull'input quando si apre la chat
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      // Ferma l'audio se la chat viene chiusa
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
      if (isRecording) {
        stopRecording();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isRecording]);

  // Reset conversazione al cambio lingua
  useEffect(() => {
    if (prevLangRef.current !== currentLang) {
      setMessages([]);
      prevLangRef.current = currentLang;
      // Reset anche lato server
      fetch(`${API_URL}/api/chat/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      }).catch(() => {});
    }
  }, [currentLang, sessionId]);

  // Ascolta evento custom per aprire la chat (da Navbar, AICoachSection, ecc.)
  useEffect(() => {
    const handleOpenChat = (e: Event) => {
      setIsOpen(true);
      // Se l'evento porta un messaggio precompilato
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.message) {
        setInputValue(customEvent.detail.message);
      }
    };

    window.addEventListener('open-ai-chat', handleOpenChat);
    return () => window.removeEventListener('open-ai-chat', handleOpenChat);
  }, []);



  // --- LOGICA TESTUALE ---
  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isLoading || isRecording) return;

    const userMessage: Message = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          lang: currentLang,
          sessionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.text) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.text }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error || ui.error }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: ui.error }]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // --- LOGICA VOCALE CON VAD (Voice Activity Detection) ---
  const vadTimeoutRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const isSpeakingRef = useRef(false);

  // Soglia di rumore (RMS) e tempo di silenzio per auto-invio
  const VOLUME_THRESHOLD = 0.02; 
  const SILENCE_DURATION_MS = 800;

  const startRecording = async () => {
    // Don't start if chat is closed or already recording or loading
    if (!isOpen || isLoading) return;
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Setup VAD
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.2;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const checkVolume = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(dataArray);

        let sumSquares = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const normalize = (dataArray[i] / 128.0) - 1.0;
          sumSquares += normalize * normalize;
        }
        const rms = Math.sqrt(sumSquares / dataArray.length);

        if (rms > VOLUME_THRESHOLD) {
          // L'utente sta parlando
          isSpeakingRef.current = true;
          if (vadTimeoutRef.current) {
            window.clearTimeout(vadTimeoutRef.current);
            vadTimeoutRef.current = null;
          }
        } else {
          // Silenzio rilevato
          if (isSpeakingRef.current && !vadTimeoutRef.current) {
            // L'utente ha smesso di parlare, avvio il timer di riempimento
            vadTimeoutRef.current = window.setTimeout(() => {
              // Timer scaduto: ferma tutto e invia
              stopRecordingAndSend();
            }, SILENCE_DURATION_MS);
          }
        }

        rafRef.current = requestAnimationFrame(checkVolume);
      };

      // Avvio la raccolta audio "fisica"
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      isSpeakingRef.current = false; // reset ad ogni avvio

      // Avvio loop VAD
      checkVolume();

    } catch (err) {
      console.error("Errore accesso microfono:", err);
      alert("Impossibile accedere al microfono. Verifica i permessi e riprova.");
    }
  };

  const stopRecordingAndSend = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        sendVoiceMessage(audioBlob);
        
        // Pulizia stream
        mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
      };
      
      cleanupVAD();
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    // Stop forzato dall'utente (es. clicca il pulsante stop rotondo rosso)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
      };
      cleanupVAD();
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cleanupVAD = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (vadTimeoutRef.current) {
      window.clearTimeout(vadTimeoutRef.current);
      vadTimeoutRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  // Keep ref always up to date with latest startRecording
  startRecordingRef.current = startRecording;

  // Autoplay nuova gestione con auto-riavvio microfono
  const playBase64Audio = (base64: string) => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }
    const audioUrl = `data:audio/mp3;base64,${base64}`;
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;
    
    // Auto-riavvia il microfono dopo che l'IA ha finito di parlare
    audio.onended = () => {
      // Usa il ref per evitare stale closures
      setTimeout(() => startRecordingRef.current(), 300);
    };

    audio.play().catch(e => {
      console.error("Errore riproduzione:", e);
      // Se la riproduzione fallisce, riavvia comunque il microfono
      setTimeout(() => startRecordingRef.current(), 300);
    });
  };

  const sendVoiceMessage = async (audioBlob: Blob) => {
    setIsLoading(true);
    
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice.webm');
    formData.append('lang', currentLang);
    formData.append('sessionId', sessionId);

    try {
      const response = await fetch(`${API_URL}/api/chat/voice`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.text) {
        if (data.userText) {
           setMessages(prev => [...prev, { role: 'user', content: data.userText }]);
        }
        setMessages(prev => [...prev, { role: 'assistant', content: data.text }]);
        
        if (data.audioBase64) {
          playBase64Audio(data.audioBase64);
        } else {
          // Se manca l'audio, riavvia il microfono dopo un breve delay
          setTimeout(() => startRecordingRef.current(), 500);
        }
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error || ui.error }]);
      }
    } catch (e) {
      console.error(e);
      setMessages(prev => [...prev, { role: 'assistant', content: ui.error }]);
      // Anche in caso di errore, riavvia il microfono per continuare il dialogo
      setTimeout(() => startRecordingRef.current(), 1000);
    } finally {
      setIsLoading(false);
    }
  };

  const resetChat = async () => {
    setMessages([]);
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }
    try {
      await fetch(`${API_URL}/api/chat/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
    } catch { /* silenzioso */ }
  };

  return (
    <>
      {/* Pulsante flottante */}
      <button
        id="ai-chat-fab"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[998] w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 cursor-pointer group ${
          isOpen
            ? 'bg-gray-700 hover:bg-gray-800 rotate-0'
            : 'bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 hover:scale-110'
        }`}
        aria-label={isOpen ? 'Chiudi chat' : 'Apri chat IA'}
      >
        {isOpen ? (
          <i className="ri-close-line text-white text-2xl"></i>
        ) : (
          <>
            <i className="ri-chat-voice-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
            {/* Dot di notifica */}
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
          </>
        )}
      </button>

      {/* Pannello Chat */}
      <div
        className={`fixed bottom-24 right-6 z-[997] w-[380px] max-w-[calc(100vw-2rem)] transition-all duration-300 origin-bottom-right ${
          isOpen
            ? 'scale-100 opacity-100 pointer-events-auto'
            : 'scale-95 opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col" style={{ height: '520px' }}>
          
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                {isRecording ? (
                   <i className="ri-mic-fill text-white text-xl animate-pulse"></i>
                ) : (
                   <i className="ri-sparkling-2-line text-white text-xl"></i>
                )}
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">{ui.title}</h3>
                <p className="text-white/70 text-xs">{isRecording ? "In ascolto... (parla liberamente)" : ui.poweredBy}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={resetChat}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer"
                title={ui.reset}
              >
                <i className="ri-refresh-line text-white text-sm"></i>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-sm"></i>
              </button>
            </div>
          </div>

          {/* Messaggi */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {/* Messaggio di benvenuto */}
            {messages.length === 0 && (
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-sparkling-2-line text-white text-sm"></i>
                </div>
                <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border border-gray-100 max-w-[80%]">
                  <p className="text-gray-700 text-sm leading-relaxed">{ui.welcome}</p>
                </div>
              </div>
            )}

            {/* Messaggi della conversazione */}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-sparkling-2-line text-white text-sm"></i>
                  </div>
                )}
                <div
                  className={`rounded-2xl px-4 py-3 max-w-[80%] text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-tr-md'
                      : 'bg-white text-gray-700 rounded-tl-md shadow-sm border border-gray-100'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 bg-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-gray-600 text-sm"></i>
                  </div>
                )}
              </div>
            ))}

            {/* Indicatore di caricamento */}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-sparkling-2-line text-white text-sm"></i>
                </div>
                <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-white border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? "In ascolto... (parla liberamente)" : ui.placeholder}
                disabled={isLoading || isRecording}
                className="flex-1 px-4 py-2.5 bg-gray-100 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:bg-white transition-all disabled:opacity-50"
              />
              
              {/* Pulsante Microfono */}
              <button
                onClick={isRecording ? stopRecordingAndSend : startRecording}
                disabled={isLoading}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-40 disabled:hover:scale-100 cursor-pointer ${
                  isRecording 
                    ? 'bg-red-500 text-white animate-pulse shadow-lg' 
                    : inputValue.trim() 
                      ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' 
                      : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md hover:shadow-lg hover:scale-105'
                }`}
                title={isRecording ? "Ferma registrazione" : "Parla"}
              >
                {isRecording ? (
                  <i className="ri-stop-fill text-lg"></i>
                ) : (
                  <i className="ri-mic-line text-lg"></i>
                )}
              </button>

              {/* Pulsante Invio Testuale */}
              {inputValue.trim() && !isRecording && (
                <button
                  onClick={sendMessage}
                  disabled={isLoading}
                  className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white hover:shadow-lg hover:scale-105 transition-all disabled:opacity-40 disabled:hover:scale-100 cursor-pointer"
                >
                  <i className="ri-send-plane-2-fill text-sm"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
